// What /conformidade keeps in its URL: the ecosystem, the tab (`?aba=`) and the OPIN governance
// section (`?secao=`), so a reload, the back button or a shared link opens the same view. A value
// equal to its default is left out of the URL.
import { Option, Schema } from 'effect';
import { Ecosystem } from '@finnest/power-admin-contracts';
import { DEFAULT_WORKSPACE_TAB, OpinWorkspaceTab } from '../../opin/core/governance-probes.tsx';
import type { EcosystemControl } from '../../regulatory/ecosystem/requested-ecosystem.tsx';
import { EcosystemSearch } from './ecosystem-search.ts';

/** Conformidade's tabs. */
const ConformidadeTab = Schema.Literals(['engines', 'governanca', 'vigilancia']);
type ConformidadeTab = typeof ConformidadeTab.Type;

const DEFAULT_TAB = 'engines' satisfies ConformidadeTab;

export const ConformidadeSearch = Schema.Struct({
  ...EcosystemSearch.fields,
  aba: Schema.optionalKey(ConformidadeTab),
  secao: Schema.optionalKey(OpinWorkspaceTab),
});
export type ConformidadeSearch = typeof ConformidadeSearch.Type;

// The same URL, each key read as present or not: encoding `None` leaves the key out.
const ConformidadeUrl = Schema.Struct({
  ecossistema: Schema.OptionFromOptionalKey(Ecosystem),
  aba: Schema.OptionFromOptionalKey(ConformidadeTab),
  secao: Schema.OptionFromOptionalKey(OpinWorkspaceTab),
});
const encodeUrl = Schema.encodeSync(ConformidadeUrl);

/** What the Conformidade page shows from its link, and how it asks for another view. */
export type ConformidadeControl = EcosystemControl & {
  readonly tab: ConformidadeTab;
  readonly onTab: (tab: ConformidadeTab) => void;
  /** The OPIN governance section the Governança tab shows. */
  readonly workspaceTab: OpinWorkspaceTab;
  readonly onWorkspaceTab: (tab: OpinWorkspaceTab) => void;
};

/** The view a Conformidade link opens. */
export type ConformidadeLink = {
  readonly ecosystem: Option.Option<Ecosystem>;
  readonly tab: ConformidadeTab;
  readonly workspaceTab: OpinWorkspaceTab;
};

/** The view `search` names; what it leaves out is the default. */
export const conformidadeLinkOf = (search: ConformidadeSearch): ConformidadeLink => ({
  ecosystem: Option.fromUndefinedOr(search.ecossistema),
  tab: search.aba ?? DEFAULT_TAB,
  workspaceTab: search.secao ?? DEFAULT_WORKSPACE_TAB,
});

/** The search of a link to `link`, naming only what differs from the default view. */
export const conformidadeSearchOf = ({
  ecosystem,
  tab,
  workspaceTab,
}: ConformidadeLink): ConformidadeSearch =>
  encodeUrl({
    ecossistema: ecosystem,
    aba: Option.liftPredicate(tab, (asked) => asked !== DEFAULT_TAB),
    secao: Option.liftPredicate(workspaceTab, (asked) => asked !== DEFAULT_WORKSPACE_TAB),
  });

/** Another ecosystem keeps the tab and drops the section, which belongs to the one left. */
export const withEcosystem = (
  link: ConformidadeLink,
  ecosystem: Option.Option<Ecosystem>,
): ConformidadeLink => ({ ...link, ecosystem, workspaceTab: DEFAULT_WORKSPACE_TAB });
